// v10
// Path: frontend/src/components/SyncHub/PipelineConfigModal.jsx
// Inputs: token, config, accounts = [], dbProfiles = [], initialDefaults = {}, onClose, onSaved
// Outputs: React JSX modal for creating or editing sync pipeline configurations with explicit container selections, independent org filters, and dynamic entity introspection
// Summary: Generalized pipeline configuration modal supporting explicit source/target container selection, independent company filtering, dynamic catalog entity loading, standard presets, and manual field mapping.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import SearchableSelect from './SearchableSelect';
import { useSyncStore } from './useSyncStore';

// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

export const PipelineConfigModal = ({
    token,
    config,
    accounts = [],
    dbProfiles = [],
    initialDefaults = {},
    onClose,
    onSaved
}) => {
    const [loading, setLoading] = useState(false);

    // Containers & Organizations from Data Catalog Studio
    const [catalogConnections, setCatalogConnections] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loadingConnections, setLoadingConnections] = useState(true);

    // Selected Container IDs (strictly explicit - no auto preselection)
    const [selectedSourceContainerId, setSelectedSourceContainerId] = useState(
        config?.SourceContainerID ? String(config.SourceContainerID) : (initialDefaults.sourceContainerId ? String(initialDefaults.sourceContainerId) : '')
    );
    const [selectedTargetContainerId, setSelectedTargetContainerId] = useState(
        config?.TargetContainerID ? String(config.TargetContainerID) : (initialDefaults.targetContainerId ? String(initialDefaults.targetContainerId) : '')
    );

    // Independent Company/Org filters
    const [sourceOrgFilter, setSourceOrgFilter] = useState('');
    const [targetOrgFilter, setTargetOrgFilter] = useState('');

    // Dynamic Source & Target Entities
    const [sourceEntities, setSourceEntities] = useState([]);
    const [loadingSourceEntities, setLoadingSourceEntities] = useState(false);
    const [selectedSourceEntity, setSelectedSourceEntity] = useState(config?.TableName || initialDefaults.tableName || '');
    const [selectedSourceEntityId, setSelectedSourceEntityId] = useState(config?.SourceEntityID || null);

    const [targetEntities, setTargetEntities] = useState([]);
    const [loadingTargetEntities, setLoadingTargetEntities] = useState(false);
    const [selectedTargetEntity, setSelectedTargetEntity] = useState(config?.TargetQBEntity || config?.EntityType || initialDefaults.entityType || '');
    const [selectedTargetEntityId, setSelectedTargetEntityId] = useState(config?.TargetEntityID || null);

    // Standard Mapping Presets
    const [standardPresets, setStandardPresets] = useState([]);
    const [availableSets, setAvailableSets] = useState([]);
    const [selectedSetName, setSelectedSetName] = useState('');
    const [loadingPresets, setLoadingPresets] = useState(false);
    const [mappingMode, setMappingMode] = useState('standard'); // 'standard' or 'custom'
    const [fieldMappings, setFieldMappings] = useState(config?.FieldMappingsJson ? JSON.parse(config.FieldMappingsJson) : []);

    // Form fields
    const [configName, setConfigName] = useState(config?.ConfigName || '');
    const [syncDirection, setSyncDirection] = useState(config?.SyncDirection || 'PBS_TO_QB');
    const [conflictPolicy, setConflictPolicy] = useState(config?.ConflictPolicy || 'SOURCE_WINS');
    const [isActive, setIsActive] = useState(config ? !!config.IsActive : true);

    // Diagnostics & Credential testing section
    const [showDiagnostics, setShowDiagnostics] = useState(false);
    const [testingConn, setTestingConn] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const isGroupAdd = !!initialDefaults.isGroupAdd;

    // ── 1. Fetch Data Catalog Studio Connections & Organizations ────────────────
    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            setLoadingConnections(true);
            try {
                const [connsRes, orgsRes] = await Promise.all([
                    fetch('/api/catalog/connections', { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch('/api/catalog/organizations', { headers: { 'Authorization': `Bearer ${token}` } })
                ]);

                if (connsRes.ok) {
                    const data = await unbox(connsRes);
                    const conns = Array.isArray(data) ? data : (data.data || []);
                    if (isMounted) setCatalogConnections(conns);
                }

                if (orgsRes.ok) {
                    const oData = await unbox(orgsRes);
                    const orgs = Array.isArray(oData) ? oData : (oData.data || []);
                    if (isMounted) setOrganizations(orgs);
                }
            } catch (err) {
                console.warn('[PipelineConfigModal] Error loading catalog data:', err);
            } finally {
                if (isMounted) setLoadingConnections(false);
            }
        };
        fetchData();
        return () => { isMounted = false; };
    }, [token]);

    // Deduplicate unique containers by ContainerID
    const uniqueContainers = useMemo(() => {
        const map = new Map();
        (catalogConnections || []).forEach(c => {
            if (!c.ContainerID) return;
            if (!map.has(c.ContainerID) || (!map.get(c.ContainerID).CredentialID && c.CredentialID)) {
                map.set(c.ContainerID, c);
            }
        });
        return Array.from(map.values());
    }, [catalogConnections]);

    // Active Source and Target Container objects
    const sourceContainer = useMemo(() => {
        return uniqueContainers.find(c => String(c.ContainerID) === String(selectedSourceContainerId)) || null;
    }, [uniqueContainers, selectedSourceContainerId]);

    // Auto-synchronize sourceOrgFilter and targetOrgFilter with selected containers
    const prevSourceContainerIdRef = useRef(null);
    useEffect(() => {
        if (sourceContainer && prevSourceContainerIdRef.current !== selectedSourceContainerId) {
            prevSourceContainerIdRef.current = selectedSourceContainerId;
            const orgId = sourceContainer.EffectiveOrganizationID || sourceContainer.ContainerOrgID || sourceContainer.InstanceOrgID || sourceContainer.HostOrgID;
            if (orgId) {
                setSourceOrgFilter(String(orgId));
            }
        }
    }, [sourceContainer, selectedSourceContainerId]);

    // Keyboard dismiss on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const prevTargetContainerIdRef = useRef(null);
    useEffect(() => {
        if (targetContainer && prevTargetContainerIdRef.current !== selectedTargetContainerId) {
            prevTargetContainerIdRef.current = selectedTargetContainerId;
            const orgId = targetContainer.EffectiveOrganizationID || targetContainer.ContainerOrgID || targetContainer.InstanceOrgID || targetContainer.HostOrgID;
            if (orgId) {
                setTargetOrgFilter(String(orgId));
            }
        }
    }, [targetContainer, selectedTargetContainerId]);

    // Filter available source containers based on independent sourceOrgFilter
    const availableSourceContainers = useMemo(() => {
        if (!sourceOrgFilter) return uniqueContainers;
        return uniqueContainers.filter(c => {
            const orgId = c.EffectiveOrganizationID || c.ContainerOrgID || c.InstanceOrgID || c.HostOrgID;
            return String(orgId) === String(sourceOrgFilter) || String(c.OrganizationName) === String(sourceOrgFilter);
        });
    }, [uniqueContainers, sourceOrgFilter]);

    // Filter available target containers based on independent targetOrgFilter
    const availableTargetContainers = useMemo(() => {
        if (!targetOrgFilter) return uniqueContainers;
        return uniqueContainers.filter(c => {
            const orgId = c.EffectiveOrganizationID || c.ContainerOrgID || c.InstanceOrgID || c.HostOrgID;
            return String(orgId) === String(targetOrgFilter) || String(c.OrganizationName) === String(targetOrgFilter);
        });
    }, [uniqueContainers, targetOrgFilter]);

    // Format rich descriptive container option
    const formatContainerOption = (c) => {
        const credPart = c.CredentialName ? ` [${c.CredentialName}]` : '';
        const hostPart = c.HostDisplayName || c.HostName || c.Hostname || 'Endpoint';
        const instPart = c.InstanceName ? ` on ${c.InstanceName}` : '';
        const orgPart = c.OrganizationName || 'Unassigned Org';
        const appPart = c.SourceAppName || c.SourceAppCode || 'App';

        return {
            value: String(c.ContainerID),
            label: `${c.ContainerName}${credPart}`,
            sublabel: `${orgPart} • ${appPart} • ${hostPart}${instPart}`,
            description: `${c.ContainerType || 'DATABASE'} | Port: ${c.ListenPort || 1433}`,
            isPowered: !!(c.isConfigured || c.CredentialStatus === 'CONFIGURED' || c.CredentialID)
        };
    };

    // ── 2. Fetch Entities for Source Container ──────────────────────────────────
    useEffect(() => {
        if (!sourceContainer) {
            setSourceEntities([]);
            return;
        }
        let isMounted = true;
        const fetchSourceEntities = async () => {
            setLoadingSourceEntities(true);
            try {
                let entities = [];
                if (sourceContainer.SourceAppID) {
                    const res = await fetch(`/api/catalog/entities?appId=${sourceContainer.SourceAppID}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await unbox(res);
                        entities = Array.isArray(data) ? data : (data.data || []);
                    }
                }

                // If container is relational database, also attempt to fetch physical tables
                if (entities.length === 0 && sourceContainer.ContainerName) {
                    const connPrefix = `[${sourceContainer.HostDisplayName || 'LocalServer'}|localhost|${sourceContainer.InstanceName || 'ssinstance'}|${sourceContainer.ListenPort || 1433}|sa]`;
                    const tblRes = await fetch(`/api/sync/database-tables?connPrefix=${encodeURIComponent(connPrefix)}&databaseName=${encodeURIComponent(sourceContainer.ContainerName)}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (tblRes.ok) {
                        const tblData = await unbox(tblRes);
                        const tbls = Array.isArray(tblData.tables) ? tblData.tables : [];
                        entities = tbls.map(t => ({
                            EntityID: null,
                            EntityCode: t.name || t.TableName,
                            EntityName: t.name || t.TableName
                        }));
                    }
                }

                if (isMounted) {
                    setSourceEntities(entities);
                    if (!selectedSourceEntity && entities.length > 0) {
                        setSelectedSourceEntity(entities[0].EntityCode);
                        setSelectedSourceEntityId(entities[0].EntityID);
                    }
                }
            } catch (err) {
                console.warn('[PipelineConfigModal] Error fetching source entities:', err);
            } finally {
                if (isMounted) setLoadingSourceEntities(false);
            }
        };
        fetchSourceEntities();
        return () => { isMounted = false; };
    }, [sourceContainer, token]);

    // ── 3. Fetch Entities for Target Container ──────────────────────────────────
    useEffect(() => {
        if (!targetContainer) {
            setTargetEntities([]);
            return;
        }
        let isMounted = true;
        const fetchTargetEntities = async () => {
            setLoadingTargetEntities(true);
            try {
                let entities = [];
                if (targetContainer.SourceAppID) {
                    const res = await fetch(`/api/catalog/entities?appId=${targetContainer.SourceAppID}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await unbox(res);
                        entities = Array.isArray(data) ? data : (data.data || []);
                    }
                }

                if (entities.length === 0 && targetContainer.CredentialID) {
                    // Fallback to QB account entities if QBO
                    const qbRes = await fetch(`/api/sync/accounts/${targetContainer.CredentialID}/entities`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (qbRes.ok) {
                        const qbData = await unbox(qbRes);
                        const ents = Array.isArray(qbData.entities) ? qbData.entities : [];
                        entities = ents.map(e => ({
                            EntityID: null,
                            EntityCode: e.name || e.entity,
                            EntityName: e.label || e.name || e.entity
                        }));
                    }
                }

                // Fallback default entities if needed
                if (entities.length === 0) {
                    const defaultTargets = ['Customer', 'Item', 'Invoice', 'Bill', 'Vendor', 'Account', 'Payment'];
                    entities = defaultTargets.map(name => ({ EntityID: null, EntityCode: name, EntityName: name }));
                }

                if (isMounted) {
                    setTargetEntities(entities);
                    if (!selectedTargetEntity && entities.length > 0) {
                        setSelectedTargetEntity(entities[0].EntityCode);
                        setSelectedTargetEntityId(entities[0].EntityID);
                    }
                }
            } catch (err) {
                console.warn('[PipelineConfigModal] Error fetching target entities:', err);
            } finally {
                if (isMounted) setLoadingTargetEntities(false);
            }
        };
        fetchTargetEntities();
        return () => { isMounted = false; };
    }, [targetContainer, token]);

    // ── 4. Fetch Standard Mapping Presets ────────────────────────────────────────
    const sourceAppCode = sourceContainer?.SourceAppCode || 'PBS';
    const targetAppCode = targetContainer?.SourceAppCode || 'QUICKBOOKS';

    useEffect(() => {
        let isMounted = true;
        const fetchPresets = async () => {
            setLoadingPresets(true);
            try {
                const res = await fetch(`/api/sync/standard-mappings?sourceSystem=${encodeURIComponent(sourceAppCode)}&targetSystem=${encodeURIComponent(targetAppCode)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await unbox(res);
                    const fetched = Array.isArray(data.mappings) ? data.mappings : [];
                    if (isMounted) {
                        setStandardPresets(fetched);
                        if (Array.isArray(data.availableSets)) {
                            setAvailableSets(data.availableSets);
                            if (!selectedSetName && data.availableSets.length > 0) {
                                setSelectedSetName(data.defaultSetName || data.availableSets[0]);
                            }
                        }
                    }
                }
            } catch (err) {
                console.warn('[PipelineConfigModal] Error fetching standard presets:', err);
            } finally {
                if (isMounted) setLoadingPresets(false);
            }
        };
        fetchPresets();
        return () => { isMounted = false; };
    }, [sourceAppCode, targetAppCode, token]);

    // Matched preset for selected source and target entities
    const activeMatchedPreset = useMemo(() => {
        if (!selectedTargetEntity) return null;
        const tgtKey = selectedTargetEntity.toLowerCase().trim();
        const srcKey = selectedSourceEntity ? selectedSourceEntity.toLowerCase().trim() : '';
        return standardPresets.find(p => {
            const setMatch = !selectedSetName || p.SetName === selectedSetName;
            const targetMatch = (p.TargetQBEntity || p.EntityType || '').toLowerCase().trim() === tgtKey;
            const srcMatch = !srcKey || !p.SourceTable || p.SourceTable.toLowerCase().trim() === srcKey.replace(/\d{2}$/, '');
            return setMatch && targetMatch && srcMatch;
        }) || standardPresets.find(p => {
            const setMatch = !selectedSetName || p.SetName === selectedSetName;
            const targetMatch = (p.TargetQBEntity || p.EntityType || '').toLowerCase().trim() === tgtKey;
            return setMatch && targetMatch;
        });
    }, [standardPresets, selectedTargetEntity, selectedSourceEntity, selectedSetName]);

    // Auto-apply preset mappings when switching to standard mode or changing entities
    useEffect(() => {
        if (mappingMode === 'standard' && activeMatchedPreset) {
            try {
                const parsed = JSON.parse(activeMatchedPreset.FieldMappingsJson);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setFieldMappings(parsed);
                }
            } catch (_) {}
        }
    }, [activeMatchedPreset, mappingMode]);

    // Handle Source Entity Selection
    const handleSourceEntitySelect = (entityCode) => {
        setSelectedSourceEntity(entityCode);
        const match = sourceEntities.find(e => e.EntityCode === entityCode);
        setSelectedSourceEntityId(match ? match.EntityID : null);
    };

    // Handle Target Entity Selection
    const handleTargetEntitySelect = (entityCode) => {
        setSelectedTargetEntity(entityCode);
        const match = targetEntities.find(e => e.EntityCode === entityCode);
        setSelectedTargetEntityId(match ? match.EntityID : null);
    };

    // Test Container Reachability
    const handleTestContainer = async () => {
        if (!sourceContainer) return;
        setTestingConn(true);
        setTestResult(null);
        try {
            const res = await fetch(`/api/catalog/containers/${sourceContainer.ContainerID}/test`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await unbox(res);
            setTestResult(data);
        } catch (err) {
            setTestResult({ success: false, error: err.message });
        } finally {
            setTestingConn(false);
        }
    };

    // ── Save Pipeline Configuration ─────────────────────────────────────────────
    const handleSave = async (e) => {
        e.preventDefault();
        if (!selectedSourceContainerId || !sourceContainer) {
            useSyncStore.getState().showAlert('Please select a Source Connection / Container.');
            return;
        }
        if (!selectedTargetContainerId || !targetContainer) {
            useSyncStore.getState().showAlert('Please select a Target Connection / Container.');
            return;
        }
        if (!selectedSourceEntity) {
            useSyncStore.getState().showAlert('Please select a Source Entity or Table.');
            return;
        }
        if (!selectedTargetEntity) {
            useSyncStore.getState().showAlert('Please select a Target Entity.');
            return;
        }

        setLoading(true);
        try {
            const hostDisp = sourceContainer.HostDisplayName || sourceContainer.HostName || 'LocalServer';
            const hostIp = sourceContainer.HostName || 'localhost';
            const svc = sourceContainer.InstanceName || 'ssinstance';
            const port = sourceContainer.ListenPort || 1433;
            const derivedConnPrefix = `[${hostDisp}|${hostIp}|${svc}|${port}|sa]`;

            const defaultName = `${sourceContainer.ContainerName} [${selectedSourceEntity}] ➔ ${targetContainer.ContainerName} [${selectedTargetEntity}]`;
            const payload = {
                configId: config ? config.ConfigID : undefined,
                configName: configName.trim() || defaultName,
                sourceContainerId: parseInt(selectedSourceContainerId, 10),
                targetContainerId: parseInt(selectedTargetContainerId, 10),
                sourceEntityId: selectedSourceEntityId || undefined,
                targetEntityId: selectedTargetEntityId || undefined,
                tableName: selectedSourceEntity,
                targetQBEntity: selectedTargetEntity,
                entityType: selectedTargetEntity,
                sourceDatabaseName: sourceContainer.ContainerName,
                sourceConnPrefix: derivedConnPrefix,
                accountId: targetContainer.CredentialID || 1,
                fieldMappings: fieldMappings,
                syncDirection,
                conflictPolicy,
                isActive,
                connectionGroupId: initialDefaults.connectionGroupId || config?.ConnectionGroupID || undefined
            };

            const res = await fetch('/api/sync/configs', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errData = await unbox(res);
                throw new Error(errData.error || 'Failed to save pipeline configuration.');
            }

            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            useSyncStore.getState().showAlert(`Save error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div 
            onClick={onClose}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto"
        >
            <div 
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-850 border border-slate-700 rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-4 my-8"
            >
                {/* Header */}
                <div className="flex justify-between items-center border-b border-slate-700 pb-3">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400">
                            <span className="material-symbols-outlined text-[18px]">table_chart</span>
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-white">
                                {config ? 'Edit Table Sync Pipeline' : (isGroupAdd ? 'Add Entity Pipeline to Group' : 'New Table Sync Pipeline')}
                            </h3>
                            <p className="text-[11px] text-slate-400">
                                Connect source and target data containers cataloged in Data Catalog Studio.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" title="Close Modal">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Locked Group Context Notice if adding inside an existing group */}
                {isGroupAdd && sourceContainer && targetContainer && (
                    <div className="p-3 bg-blue-950/60 border border-blue-800/80 rounded-xl text-xs space-y-1">
                        <div className="text-blue-300 font-bold uppercase tracking-wider flex items-center gap-1.5 text-[10px]">
                            <span className="material-symbols-outlined text-[14px]">hub</span>
                            Target Connection Group (Locked)
                        </div>
                        <div className="flex items-center gap-2 text-slate-200 font-mono text-[11px] flex-wrap">
                            <span className="bg-slate-900 px-2 py-0.5 rounded border border-slate-700 text-sky-300 font-bold">
                                {sourceContainer.ContainerName}
                            </span>
                            <span>&bull;</span>
                            <span className="text-slate-400">&rarr;</span>
                            <span className="bg-slate-900 px-2 py-0.5 rounded border border-slate-700 text-emerald-300 font-bold">
                                {targetContainer.ContainerName}
                            </span>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSave} className="space-y-4">
                    {/* Pipeline Name / Description */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                            Pipeline Name / Label
                        </label>
                        <input
                            type="text"
                            placeholder={sourceContainer && targetContainer ? `${sourceContainer.ContainerName} [${selectedSourceEntity || 'Entity'}] ➔ ${targetContainer.ContainerName} [${selectedTargetEntity || 'Entity'}]` : 'e.g. PBS Customers ➔ QBO Sandbox'}
                            value={configName}
                            onChange={(e) => setConfigName(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                        />
                    </div>

                    {/* Source & Target Container Selectors */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Source Container Selector */}
                        <div className="p-3 bg-slate-900/60 border border-slate-800 rounded-xl space-y-2">
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                Source Connection / Container <span className="text-red-400">*</span>
                            </label>
                            {isGroupAdd ? (
                                <div className="p-2.5 bg-slate-950 border border-slate-800 rounded-lg text-xs font-mono text-sky-300">
                                    {sourceContainer ? `${sourceContainer.ContainerName} (${sourceContainer.SourceAppName || 'PBS'})` : 'Source Container Locked'}
                                </div>
                            ) : (
                                <>
                                    {/* Independent Organization Filter for Source */}
                                    <div>
                                        <label className="block text-[11px] font-medium text-slate-400 mb-1">
                                            Filter by Source Company / Org:
                                        </label>
                                        <SearchableSelect
                                            disabled={loadingConnections}
                                            placeholder="All Companies / Orgs"
                                            value={sourceOrgFilter}
                                            onChange={(val) => setSourceOrgFilter(val)}
                                            options={[
                                                { value: '', label: 'All Companies / Organizations' },
                                                ...organizations.map(org => ({
                                                    value: String(org.OrganizationID),
                                                    label: org.OrganizationName,
                                                    sublabel: org.OrganizationType || 'Organization'
                                                }))
                                            ]}
                                        />
                                    </div>
                                    <SearchableSelect
                                        disabled={loadingConnections}
                                        placeholder={loadingConnections ? 'Loading containers...' : '-- Choose Source Container --'}
                                        value={selectedSourceContainerId}
                                        onChange={(val) => {
                                            setSelectedSourceContainerId(val);
                                            setSelectedSourceEntity('');
                                            setSelectedSourceEntityId(null);
                                        }}
                                        options={availableSourceContainers.map(formatContainerOption)}
                                    />
                                </>
                            )}
                            {sourceContainer && (
                                <span className="text-[10px] text-slate-400 mt-1 block">
                                    App: <strong className="text-slate-300">{sourceContainer.SourceAppName || sourceContainer.SourceAppCode}</strong> &bull; Host: {sourceContainer.HostDisplayName || sourceContainer.HostName}
                                </span>
                            )}
                        </div>

                        {/* Target Container Selector */}
                        <div className="p-3 bg-slate-900/60 border border-slate-800 rounded-xl space-y-2">
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                Target Connection / Container <span className="text-red-400">*</span>
                            </label>
                            {isGroupAdd ? (
                                <div className="p-2.5 bg-slate-950 border border-slate-800 rounded-lg text-xs font-mono text-emerald-300">
                                    {targetContainer ? `${targetContainer.ContainerName} (${targetContainer.SourceAppName || 'QBO'})` : 'Target Container Locked'}
                                </div>
                            ) : (
                                <>
                                    {/* Independent Organization Filter for Target */}
                                    <div>
                                        <label className="block text-[11px] font-medium text-slate-400 mb-1">
                                            Filter by Target Company / Org:
                                        </label>
                                        <SearchableSelect
                                            disabled={loadingConnections}
                                            placeholder="All Companies / Orgs"
                                            value={targetOrgFilter}
                                            onChange={(val) => setTargetOrgFilter(val)}
                                            options={[
                                                { value: '', label: 'All Companies / Organizations' },
                                                ...organizations.map(org => ({
                                                    value: String(org.OrganizationID),
                                                    label: org.OrganizationName,
                                                    sublabel: org.OrganizationType || 'Organization'
                                                }))
                                            ]}
                                        />
                                    </div>
                                    <SearchableSelect
                                        disabled={loadingConnections}
                                        placeholder={loadingConnections ? 'Loading containers...' : '-- Choose Target Container --'}
                                        value={selectedTargetContainerId}
                                        onChange={(val) => {
                                            setSelectedTargetContainerId(val);
                                            setSelectedTargetEntity('');
                                            setSelectedTargetEntityId(null);
                                        }}
                                        options={availableTargetContainers.map(formatContainerOption)}
                                    />
                                </>
                            )}
                            {targetContainer && (
                                <span className="text-[10px] text-slate-400 mt-1 block">
                                    App: <strong className="text-emerald-300">{targetContainer.SourceAppName || targetContainer.SourceAppCode}</strong> &bull; Type: {targetContainer.ContainerType}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Source Entity & Target Entity Selectors */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                        {/* Source Entity */}
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Source Entity / Table <span className="text-red-400">*</span>
                            </label>
                            <SearchableSelect
                                disabled={!selectedSourceContainerId || loadingSourceEntities}
                                placeholder={loadingSourceEntities ? 'Loading source entities...' : (!selectedSourceContainerId ? 'Select source container first' : '-- Select Source Entity --')}
                                value={selectedSourceEntity}
                                onChange={handleSourceEntitySelect}
                                allowCustom={true}
                                customPrompt="Use custom table name"
                                options={sourceEntities.map(e => ({
                                    value: e.EntityCode,
                                    label: e.EntityCode,
                                    sublabel: e.EntityName && e.EntityName !== e.EntityCode ? e.EntityName : undefined
                                }))}
                            />
                            <span className="text-[10px] text-slate-400 mt-1 block">
                                Catalogued entity or underlying database table.
                            </span>
                        </div>

                        {/* Target Entity */}
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                                Target Destination Entity <span className="text-red-400">*</span>
                            </label>
                            <SearchableSelect
                                disabled={!selectedTargetContainerId || loadingTargetEntities}
                                placeholder={loadingTargetEntities ? 'Loading target entities...' : (!selectedTargetContainerId ? 'Select target container first' : '-- Select Target Entity --')}
                                value={selectedTargetEntity}
                                onChange={handleTargetEntitySelect}
                                allowCustom={true}
                                customPrompt="Use custom target entity"
                                options={targetEntities.map(e => ({
                                    value: e.EntityCode,
                                    label: e.EntityCode,
                                    sublabel: e.EntityName && e.EntityName !== e.EntityCode ? e.EntityName : undefined
                                }))}
                            />
                            <span className="text-[10px] text-slate-400 mt-1 block">
                                Destination record schema in target application.
                            </span>
                        </div>
                    </div>

                    {/* Field Mapping Strategy: Standard Preset vs Manual */}
                    <div className="bg-slate-900 border border-slate-700/80 rounded-xl p-3.5 space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-200 uppercase tracking-wider flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-[16px] text-indigo-400">tune</span>
                                Field Mapping Configuration
                            </span>
                            <div className="flex items-center gap-1 bg-slate-950 p-0.5 rounded-lg border border-slate-800 text-[11px]">
                                <button
                                    type="button"
                                    onClick={() => setMappingMode('standard')}
                                    className={`px-2.5 py-1 rounded font-semibold transition-colors ${mappingMode === 'standard' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                                >
                                    Standard Preset
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMappingMode('custom')}
                                    className={`px-2.5 py-1 rounded font-semibold transition-colors ${mappingMode === 'custom' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                                >
                                    Manual Mapping
                                </button>
                            </div>
                        </div>

                        {mappingMode === 'standard' ? (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs text-slate-400">Preset Set:</span>
                                    {availableSets.length > 0 ? (
                                        <select
                                            value={selectedSetName}
                                            onChange={(e) => setSelectedSetName(e.target.value)}
                                            className="bg-slate-800 border border-slate-700 rounded px-2.5 py-1 text-xs text-white focus:outline-none focus:border-blue-500"
                                        >
                                            {availableSets.map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="text-xs text-slate-500 italic">Default Production Rules</span>
                                    )}
                                </div>

                                {activeMatchedPreset ? (
                                    <div className="p-2.5 bg-emerald-950/40 border border-emerald-800/60 rounded-lg text-xs space-y-1">
                                        <div className="text-emerald-300 font-bold flex items-center gap-1.5">
                                            <span className="material-symbols-outlined text-[15px]">verified</span>
                                            Standard Preset Available ({fieldMappings.length} fields mapped)
                                        </div>
                                        <div className="text-slate-300 text-[11px] font-mono flex items-center gap-1.5 flex-wrap">
                                            {fieldMappings.slice(0, 5).map(m => (
                                                <span key={m.target || m.field} className="px-1.5 py-0.5 rounded bg-slate-900 border border-slate-700">
                                                    {m.source || m.expression || 'auto'} &rarr; {m.target || m.field}
                                                </span>
                                            ))}
                                            {fieldMappings.length > 5 && (
                                                <span className="text-slate-400 font-sans">+{fieldMappings.length - 5} more fields</span>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-2.5 bg-slate-800/80 border border-slate-700 rounded-lg text-xs text-slate-400 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-amber-400 text-[16px]">info</span>
                                        <span>No standard preset found for {selectedSourceEntity || 'entity'} &rarr; {selectedTargetEntity || 'target'}. You can configure custom field mappings after creating the pipeline.</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="p-2.5 bg-slate-800/80 border border-slate-700 rounded-lg text-xs text-slate-300 space-y-1">
                                <p className="font-semibold text-white">Manual Field Mapping Designer</p>
                                <p className="text-[11px] text-slate-400">
                                    Once saved, this pipeline can be customized with complete drag-and-drop field mappings, transforms, and conditional rules in the visual Field Mapping Designer.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Conflict Policy, Direction & Active Toggle */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                        <div>
                            <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                                Sync Direction
                            </label>
                            <select
                                value={syncDirection}
                                onChange={(e) => setSyncDirection(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="PBS_TO_QB">Source ➔ Target (One-Way)</option>
                                <option value="QB_TO_PBS">Target ➔ Source (Reverse)</option>
                                <option value="BIDIRECTIONAL">Bidirectional Sync</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                                Conflict Resolution
                            </label>
                            <select
                                value={conflictPolicy}
                                onChange={(e) => setConflictPolicy(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="SOURCE_WINS">Source Wins (Overwrite Target)</option>
                                <option value="TARGET_WINS">Target Wins (Preserve Target)</option>
                                <option value="MANUAL_REVIEW">Flag for Manual Review</option>
                            </select>
                        </div>

                        <div className="flex flex-col justify-end pb-1">
                            <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-slate-300 font-semibold">
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                    className="w-4 h-4 rounded text-blue-600 focus:ring-0 focus:outline-none bg-slate-900 border-slate-700 cursor-pointer"
                                />
                                <span>Pipeline Enabled &amp; Active</span>
                            </label>
                        </div>
                    </div>

                    {/* Diagnostics Drawer (Optional for SQL Server source databases) */}
                    {sourceContainer?.ContainerType === 'RELATIONAL_DATABASE' && (
                        <div className="border border-slate-700/60 rounded-xl overflow-hidden bg-slate-900/60">
                            <button
                                type="button"
                                onClick={() => setShowDiagnostics(!showDiagnostics)}
                                className="w-full px-3 py-2 text-left flex items-center justify-between text-xs text-slate-400 hover:text-white transition-colors"
                            >
                                <span className="flex items-center gap-1.5 font-semibold">
                                    <span className="material-symbols-outlined text-[15px] text-amber-400">electrical_services</span>
                                    Connection Reachability &amp; Diagnostics
                                </span>
                                <span className="material-symbols-outlined text-[16px]">
                                    {showDiagnostics ? 'expand_less' : 'expand_more'}
                                </span>
                            </button>
                            {showDiagnostics && (
                                <div className="p-3 border-t border-slate-700/60 space-y-2 text-xs">
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-300">
                                            Test reachability to <strong>{sourceContainer.ContainerName}</strong>:
                                        </span>
                                        <button
                                            type="button"
                                            onClick={handleTestContainer}
                                            disabled={testingConn}
                                            className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-600 rounded text-xs font-semibold flex items-center gap-1"
                                        >
                                            <span className="material-symbols-outlined text-[14px]">
                                                {testingConn ? 'sync' : 'wifi'}
                                            </span>
                                            {testingConn ? 'Testing...' : 'Test Connection'}
                                        </button>
                                    </div>
                                    {testResult && (
                                        <div className={`p-2 rounded text-[11px] font-mono ${testResult.success ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800' : 'bg-red-950/80 text-red-300 border border-red-800'}`}>
                                            {testResult.message || testResult.error || JSON.stringify(testResult)}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-3 border-t border-slate-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-all shadow-md flex items-center gap-1.5"
                        >
                            <span className="material-symbols-outlined text-[16px]">
                                {loading ? 'sync' : 'save'}
                            </span>
                            {loading ? 'Saving Pipeline...' : (config ? 'Update Pipeline' : 'Create Pipeline')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PipelineConfigModal;
